import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { dismissToast, useToasts, type Toast } from "../lib/toast";

const ICONS = { info: Info, success: CheckCircle2, warn: AlertTriangle, error: XCircle };

/** One notification: closes after its duration; hovering or focusing it pauses the timer. */
function ToastItem({ toast }: { toast: Toast }) {
  const { t } = useI18n();
  const [paused, setPaused] = useState(false);
  const left = useRef(toast.duration);
  useEffect(() => {
    if (paused) return;
    const started = Date.now();
    const timer = window.setTimeout(() => dismissToast(toast.id), left.current);
    return () => {
      window.clearTimeout(timer);
      left.current -= Date.now() - started;
    };
  }, [paused, toast.id]);
  const Icon = ICONS[toast.tone];
  return (
    <div
      className={`toast ${toast.tone}`}
      role={toast.tone === "error" ? "alert" : "status"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon size={16} aria-hidden />
      <div className="toast-body">
        <span>{toast.text}</span>
        {toast.detail && <span className="muted small">{toast.detail}</span>}
      </div>
      <button className="toast-close" onClick={() => dismissToast(toast.id)} aria-label={t("common.close")} title={t("common.close")}>
        <X size={14} aria-hidden />
      </button>
      <span className="toast-progress" style={{ animationDuration: `${toast.duration}ms`, animationPlayState: paused ? "paused" : "running" }} aria-hidden />
    </div>
  );
}

export function Toaster() {
  const toasts = useToasts();
  return (
    <div className="toaster" aria-live="polite">
      {toasts.map((item) => <ToastItem key={item.id} toast={item} />)}
    </div>
  );
}
